import React, { useContext, useEffect, useState } from "react";
import { auth } from "../../firebase";
// import firebase from "firebase/app";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";


// 创建 AuthContext，用于在组件树中共享认证状态和方法
const AuthContext = React.createContext();

// 自定义 Hook，方便在其他组件中使用认证上下文
export function useAuth() {
  return useContext(AuthContext);
}

// AuthProvider 组件，用于包裹整个应用，提供认证状态和操作方法
export function AuthProvider({ children }) {
  // currentUser: 当前登录的用户
  const [currentUser, setCurrentUser] = useState(null);
  // loading: 加载状态，初始为 true 表示正在检查用户登录状态
  const [loading, setLoading] = useState(true);

  // 传统邮箱 注册新用户，返回一个 Promise
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  // 传统邮箱 登录，返回一个 Promise
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
    .then(async (userCredential) => {
      // 检查用户是否通过邮箱验证
      if (!userCredential.user.emailVerified) {
        // 如果未验证，则登出并抛出错误
        await signOut(auth);
        throw new Error("Email not verified. Please verify your email before logging in.");
      }
      return userCredential;
    });
  }

  //登出，返回一个 Promise
  function logout() {
    return signOut(auth);
  }

  // 重置密码，返回一个 Promise
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // 更新当前用户的邮箱
  function updateEmail(email) {
    if (!currentUser) {
      // 如果没有登录用户，则返回一个拒绝的 Promise
      return Promise.reject(new Error("No user logged in"));
    }
    return firebaseUpdateEmail(currentUser, email);
  }

  // 更新当前用户的密码
  function updatePassword(password) {
    if (!currentUser) {
      return Promise.reject(new Error("No user logged in"));
    }
    return firebaseUpdatePassword(currentUser, password);
  }

  // -----------------------
  // 以下为社交登录/注册方法
  // -----------------------

// Google 注册或登录
function signUpWithGoogle() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}
function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}

// Facebook 注册或登录
function signUpWithFacebook() {
  const provider = new FacebookAuthProvider();
  return signInWithPopup(auth, provider);
}
function loginWithFacebook() {
  const provider = new FacebookAuthProvider();
  return signInWithPopup(auth, provider);
}

// Microsoft 注册或登录
// 注意：在 Firebase 控制台中需启用 Microsoft 作为 OAuth 提供程序
function signUpWithMicrosoft() {
  const provider = new OAuthProvider("microsoft.com");
  return signInWithPopup(auth, provider);
}
function loginWithMicrosoft() {
  const provider = new OAuthProvider("microsoft.com");
  return signInWithPopup(auth, provider);
}
  
  // 使用 useEffect 监听 Firebase 认证状态的变化
  useEffect(() => {
    // 注册认证状态监听器，状态改变时更新 currentUser 和 loading 状态
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    // 组件卸载时取消监听
    return () => unsubscribe();
  }, []);

  // 将认证相关的状态和方法放入 value 对象中，供下层组件使用
  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    signUpWithGoogle,
    signUpWithFacebook,
    signUpWithMicrosoft,
    loginWithGoogle,
    loginWithFacebook,
    loginWithMicrosoft,
  };

  // 仅当加载完成后，才渲染子组件，避免在认证状态未知时渲染内容
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}